import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchDesigner } from '../firebase/designerService';
import { fetchShop } from '../firebase/shopService';
import './OwnerBadge.css';
import PersonIcon from './PersonIcon';

// Who published this, and where their profile lives. Two roles publish work, so
// the byline has to resolve both — a shop's photo with no author was the one
// place the feed could show a product and no way to reach the shop selling it.
//
// DESIGNER FIRST, SHOP SECOND, and each result memoised by its own service
// (negatives included). So a designer's photo costs one read, a shop's costs two
// the first time and none after that — for the whole session, across every
// lightbox and feed tile by that author. Fetching them in parallel would make
// the common case cost two reads instead of one to save a round trip on the
// rarer one.
async function fetchOwner(uid) {
  const designer = await fetchDesigner(uid);
  if (designer) return { name: designer.fullName, avatarUrl: designer.avatarUrl, path: `/designer/${uid}` };
  const shop = await fetchShop(uid);
  if (shop) return { name: shop.name, avatarUrl: shop.avatarUrl, path: `/shop/${uid}` };
  return null;
}

// ── OWNER BADGE ──────────────────────────────────────────────────────────────
// Whose work this is, on a viewer that shows the work alone: the photo
// lightbox and the read-only 3D page. Both used to present a piece with no
// author, leaving no route from something you like to the person who made it.
//
// A LINK to their public profile — that route is the whole point.
//
// Renders NOTHING until the profile resolves, and nothing at all if it doesn't:
// a customer's document is private and a deleted account has none, and an
// "Unknown" byline is worse than no byline. The lookup is served from the
// directory caches, so a lightbox paging through twenty photos by one author
// costs a single read.
export default function OwnerBadge({ uid, className = '' }) {
  const [owner, setOwner] = useState(null);

  useEffect(() => {
    let alive = true;
    setOwner(null);
    if (!uid) return undefined;
    fetchOwner(uid).then(o => { if (alive) setOwner(o); }).catch(() => {});
    return () => { alive = false; };
  }, [uid]);

  if (!owner?.name) return null;

  return (
    <Link to={owner.path} className={`ob ${className}`.trim()}>
      <span className="ob-avatar">
        {owner.avatarUrl
          ? <img src={owner.avatarUrl} alt="" />
          : <PersonIcon />}
      </span>
      <span className="ob-name">{owner.name}</span>
    </Link>
  );
}
